import { useCallback, useMemo, useState } from 'react';

export interface UsePaginationParams {
    pageSize: number;
    totalCount: number;
}

export interface UsePaginationReturn {
    currentOffset: number;
    currentPage: number;
    paginationParams: { offset: number; limit: number };
    paginationTextVariable: { from: number; to: number; total: number };
    handlePrevPage: () => void;
    handleNextPage: () => void;
    isPrevDisabled: boolean;
    isNextDisabled: boolean;
    resetOffset: () => void;
}

export const usePagination = ({
    pageSize,
    totalCount,
}: UsePaginationParams): UsePaginationReturn => {
    const [currentOffset, setCurrentOffset] = useState(0);

    const handlePrevPage = useCallback(() => {
        const newOffset = Math.max(0, currentOffset - pageSize);

        setCurrentOffset(newOffset);
    }, [currentOffset, pageSize]);

    const handleNextPage = useCallback(() => {
        const newOffset = currentOffset + pageSize;

        if (newOffset < totalCount) {
            setCurrentOffset(newOffset);
        }
    }, [currentOffset, pageSize, totalCount]);

    const resetOffset = useCallback(() => {
        setCurrentOffset(0);
    }, []);

    const currentPage = useMemo(
        () => Math.floor(currentOffset / pageSize) + 1,
        [currentOffset, pageSize],
    );

    const paginationParams = useMemo(
        () => ({
            offset: currentOffset,
            limit: pageSize,
        }),
        [currentOffset, pageSize],
    );

    const paginationTextVariable = useMemo(
        () => ({
            from: currentOffset + 1,
            to: Math.min(currentOffset + pageSize, totalCount),
            total: totalCount,
        }),
        [currentOffset, pageSize, totalCount],
    );

    const isPrevDisabled = currentOffset === 0;
    const isNextDisabled = currentOffset + pageSize >= totalCount;

    return {
        currentOffset,
        currentPage,
        paginationParams,
        paginationTextVariable,
        handlePrevPage,
        handleNextPage,
        isPrevDisabled,
        isNextDisabled,
        resetOffset,
    };
};
